import { useQuery } from '@tanstack/react-query'
import { FormattedMessage, useIntl } from 'react-intl'
import { useLocation, useNavigate } from 'react-router'

import { BusinessRoute } from '@/constants/paths'
import { queryKeys } from '@/constants/queryKeys'
import { getFullName } from '@/lib/typography'
import { cn } from '@/lib/utils'
import { Plus } from '@/shared/icons/outline'
import {
  AnimatedFormLabel,
  Badge,
  Input,
  Select,
  SelectAnimatedPlaceholder,
  SelectContent,
  SelectItem,
  SelectTrigger,
  Skeleton,
  Typography,
} from '@/shared/ui'

import { getTeamMembers } from '../features/Team/api'
import { TeamMember, TeamState } from '../features/Team/types'
import { useBusinessUser } from '../hooks'
import { BusinessUser, Permission, Role } from '../types'

import { WithPermissions } from './WithPermissions'

type Props = {
  businessUser?: BusinessUser
  value: string
  onChange: (value: { identityId: string; teamMember?: TeamMember }) => void
  fullName?: string
  label: string
  hasError?: boolean
}

export const UserSelect = ({
  fullName,
  label,
  onChange,
  value,
  hasError = false,
}: Props) => {
  const businessUser = useBusinessUser()
  const navigate = useNavigate()
  const location = useLocation()
  const intl = useIntl()

  const getTeamMembersQuery = useQuery({
    queryKey: [queryKeys.getTeamMembers],
    queryFn: () => getTeamMembers(),
    select: (data) =>
      data.data
        .filter((member) => member.role !== Role.READ_ONLY)
        .filter((member) =>
          [TeamState.ACTIVE, TeamState.INVITED].includes(member.state),
        ),
    enabled: !fullName,
  })

  const user = getTeamMembersQuery.data?.find(
    (member) => member.identityId === value,
  )

  if (getTeamMembersQuery.isFetching) {
    return <Skeleton className="h-[54px] w-full" />
  }

  if (fullName) {
    return (
      <>
        <Input
          className="bg-neutral-gray-200"
          placeholder={intl.formatMessage({
            id: 'label.cardholder',
            defaultMessage: 'Cardholder',
          })}
          value={fullName}
          disabled
        />

        <AnimatedFormLabel>
          <FormattedMessage id="label.cardholder" defaultMessage="Cardholder" />
        </AnimatedFormLabel>
      </>
    )
  }

  return (
    <Select
      disabled={getTeamMembersQuery.data?.length === 0}
      value={value}
      onValueChange={(v) => {
        if (v === 'new-team-member-action') {
          navigate(BusinessRoute.AddTeamMember, {
            state: { from: location },
          })
          return
        }

        onChange({
          identityId: v,
          teamMember: getTeamMembersQuery.data?.find(
            (member) => member.identityId === v,
          ),
        })
      }}
    >
      <SelectTrigger
        className={cn('', {
          'border-primary-error border': hasError,
        })}
      >
        <SelectAnimatedPlaceholder
          selectedLabel={user ? getFullName(user) : label}
          placeholder={label}
          hasError={hasError}
          value={value}
        />
      </SelectTrigger>

      <SelectContent>
        <WithPermissions permissions={[Permission.TRANSFERS]}>
          <SelectItem
            value="new-team-member-action"
            className="flex items-center gap-3 px-2 py-3"
          >
            <div className="flex items-center gap-1">
              <Plus className="text-primary size-5" />
              <Typography className="text-primary font-semibold">
                <FormattedMessage
                  id="action.addTeamMember"
                  defaultMessage="Add team member"
                />
              </Typography>
            </div>
          </SelectItem>
        </WithPermissions>

        {getTeamMembersQuery.data?.map((user) => (
          <SelectItem
            disabled={user.identityId === value}
            key={user.identityId}
            value={user.identityId}
            className="flex items-center gap-3 px-2 py-3"
          >
            <Typography className="text-neutral-gray-900">
              {getFullName(user)}
            </Typography>
            {businessUser?.identityId === user.identityId && (
              <Badge className="capitalize" variant="neutral">
                <Typography bold variant="body-small">
                  <FormattedMessage id="label.you" defaultMessage="You" />
                </Typography>
              </Badge>
            )}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}
